import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import { Box, Chip, Divider, Fab, FormControl, FormControlLabel, Grid, Radio, RadioGroup, TextField, Tooltip, Typography } from '@mui/material'
import React, { FC, useState } from 'react'
import { CornerCabinetSizesSubType } from '../../../api/appAPI'
import useUnit from '../../../hooks/useUnit'
import CategorySizeAllValuesWithUnitSupport, { InputWithUnitSupport, cmToIn } from '../../common/UnitComponents'

interface ICabinetSize {
    name: 'Width 1' | 'Width 2' | 'Height' | 'Depth 1' | 'Depth 2' | 'Back Width 1' | 'Back Width 2'
    values: number[]
    defaultValue: number

    setValues(v: number[]): void
    setDefaultValue(v: number): void
}

export const CornerCabinetSize: FC<ICabinetSize> = props => {

    const { unit } = useUnit()

    const [editMode, setEditMode] = useState(false)

    const handleValueBlur = (newValue: number) => {
        if (!props.values.includes(newValue) && newValue !== 0) {
            props.setValues([...props.values, newValue])
        }
    }

    const handleEditCancel = () => {
        setEditMode(false)
    }

    const handleEditApply = (valuesInCm: number[]) => {

        props.setValues(valuesInCm)

        setEditMode(false)
    }

    return (<>
        <Divider style={{ width: '80%', marginTop: 15, marginBottom: 5 }} />
        <Box display='flex'>
            <Typography>{props.name}</Typography>
            <EditIcon fontSize='small' sx={{
                ml: 1,
                color: '#6c6c6c',
                cursor: 'pointer'
            }} onClick={() => setEditMode(true)} />
        </Box>
        <Box
            component="ul"
            px={1}
            my={1}
            style={{
                display: 'flex',
                justifyContent: 'center',
                columnGap: 10,
                rowGap: 10,
                flexWrap: 'wrap',
                listStyle: 'none'
            }}>
            {props.values.sort((a, b) => a - b).map((value) =>
                <li key={value}>
                    <Chip
                        label={unit === 'cm' ? value : cmToIn(value)}
                        onDelete={() => { props.setValues(props.values.filter(v => v !== value)) }}
                        color={value === props.defaultValue ? 'primary' : undefined}
                        onClick={() => {
                            props.setDefaultValue(value)
                        }}
                    />
                </li>)}
        </Box>

        {editMode ? <CategorySizeAllValuesWithUnitSupport
            sx={{ mt: 2 }}
            width='100%'
            valuesInCm={props.values}
            handleEditCancel={handleEditCancel}
            handleEditApply={handleEditApply}
        />
            : <InputWithUnitSupport
                label={props.name}
                sx={{ mt: 2 }}
                width='100%'
                valueInCm={0}
                setValueInCm={(v) => handleValueBlur(v)}
            />
        }
        {/* <FormControl variant="outlined" sx={{ mt: 2 }}>
            <InputLabel>{props.name}</InputLabel>
            <OutlinedInput
                style={{ width: '100%' }}
                type='number'
                label={props.name}
                value={newValue}
                onChange={(e) => {
                    setNewValue(e.target.value)
                }}
                onBlur={() => { handleValueBlur() }}
                onKeyUp={(e) => {
                    if (e.key === 'Enter')
                        handleValueBlur()
                }}
                startAdornment={<InputAdornment position="start">cm</InputAdornment>}
                inputProps={{ min: 0 }}
            />
        </FormControl> */}
    </>)
}

type TextfieldProps = {
    value: string,
    onChange(event: React.ChangeEvent<{ value: string }>): void
}

type SizesProps = {
    value: CornerCabinetSizesSubType
    onChange(value: CornerCabinetSizesSubType): void
}

interface IProps {
    title: string
    activeRole: TextfieldProps

    type: TextfieldProps

    name: TextfieldProps
    displayName: TextfieldProps
    activeRoleSizes: SizesProps

    onButtonClick(): void

    needSave?: boolean
}


const CornerCategoryUIBox: FC<IProps> = (props) => {
    return (
        <Box
            display='flex'
            flexDirection='column'
            alignItems='center'
            pb={2}
            style={{
                width: 400,
                height: 'fit-content',
                margin: 15,
                border: '1px solid black',
                maxHeight: '95%',
                overflowY: 'auto',
                overflowX: 'hidden',
                position: 'relative'
            }}
        >
            <Box
                py={1}
                style={{
                    position: 'sticky',
                    top: 0,
                    width: '100%',
                    backgroundColor: 'white',
                    zIndex: 10
                }}>
                <Grid container spacing={2}>
                    <Grid item xs={3}> </Grid>
                    <Grid item xs={6} container justifyContent='center' alignItems='center'>
                        <Typography variant='h6'>{props.title}</Typography>
                    </Grid>
                    <Grid item xs={3} container justifyContent='flex-end' alignItems='center'>
                        <Tooltip title='Save'>
                            <Fab
                                color={props.needSave ? 'secondary' : "primary"}
                                size='small'
                                onClick={props.onButtonClick}
                                style={{ marginRight: 10 }}
                            >
                                <SaveIcon />
                            </Fab>
                        </Tooltip>
                    </Grid>
                </Grid>
            </Box>

            <TextField
                label="Display Name"
                variant="outlined"
                value={props.name.value}
                onChange={props.name.onChange}
                style={{ width: '60%', marginTop: 15 }}
            />

            <TextField
                label="Category Display Name"
                variant="outlined"
                value={props.displayName.value}
                onChange={props.displayName.onChange}
                style={{ width: '60%', marginTop: 15 }}
            />

            <FormControl component="fieldset" disabled={props.title.startsWith('Edit')}>
                <RadioGroup row
                    value={props.type.value}
                    onChange={props.type.onChange}
                >
                    <FormControlLabel value="normal" control={<Radio color='primary' />} label="Normal" />
                    <FormControlLabel value="corner" control={<Radio color='primary' />} label="Corner" />
                </RadioGroup>
            </FormControl>

            <FormControl component="fieldset">
                <RadioGroup row
                    value={props.activeRole.value}
                    onChange={props.activeRole.onChange}
                >
                    <FormControlLabel value="retail" control={<Radio color='primary' />} label="Retail" />
                    <FormControlLabel value="dealer" control={<Radio color='primary' />} label="Dealer" />
                    <FormControlLabel value="designer" control={<Radio color='primary' />} label="Designer" />
                </RadioGroup>
            </FormControl>

            <CornerCabinetSize
                name='Height' values={props.activeRoleSizes.value.height.values} defaultValue={props.activeRoleSizes.value.height.default}
                setValues={(v: number[]) => {
                    props.activeRoleSizes.onChange({
                        ...props.activeRoleSizes.value,
                        height: {
                            ...props.activeRoleSizes.value.height,
                            values: v
                        }
                    })
                }}
                setDefaultValue={(v: number) => {
                    props.activeRoleSizes.onChange({
                        ...props.activeRoleSizes.value,
                        height: {
                            ...props.activeRoleSizes.value.height,
                            default: v
                        }
                    })
                }}
            />
            <CornerCabinetSize
                name='Width 1' values={props.activeRoleSizes.value.width1.values} defaultValue={props.activeRoleSizes.value.width1.default}
                setValues={(v: number[]) => {
                    props.activeRoleSizes.onChange({
                        ...props.activeRoleSizes.value,
                        width1: {
                            ...props.activeRoleSizes.value.width1,
                            values: v
                        }
                    })
                }}
                setDefaultValue={(v: number) => {
                    props.activeRoleSizes.onChange({
                        ...props.activeRoleSizes.value,
                        width1: {
                            ...props.activeRoleSizes.value.width1,
                            default: v
                        }
                    })
                }}
            />
            <CornerCabinetSize
                name='Width 2' values={props.activeRoleSizes.value.width2.values} defaultValue={props.activeRoleSizes.value.width2.default}
                setValues={(v: number[]) => {
                    props.activeRoleSizes.onChange({
                        ...props.activeRoleSizes.value,
                        width2: {
                            ...props.activeRoleSizes.value.width2,
                            values: v
                        }
                    })
                }}
                setDefaultValue={(v: number) => {
                    props.activeRoleSizes.onChange({
                        ...props.activeRoleSizes.value,
                        width2: {
                            ...props.activeRoleSizes.value.width2,
                            default: v
                        }
                    })
                }}
            />
            <CornerCabinetSize
                name='Depth 1' values={props.activeRoleSizes.value.depth1.values} defaultValue={props.activeRoleSizes.value.depth1.default}
                setValues={(v: number[]) => {
                    props.activeRoleSizes.onChange({
                        ...props.activeRoleSizes.value,
                        depth1: {
                            ...props.activeRoleSizes.value.depth1,
                            values: v
                        }
                    })
                }}
                setDefaultValue={(v: number) => {
                    props.activeRoleSizes.onChange({
                        ...props.activeRoleSizes.value,
                        depth1: {
                            ...props.activeRoleSizes.value.depth1,
                            default: v
                        }
                    })
                }}
            />
            <CornerCabinetSize
                name='Depth 2' values={props.activeRoleSizes.value.depth2.values} defaultValue={props.activeRoleSizes.value.depth2.default}
                setValues={(v: number[]) => {
                    props.activeRoleSizes.onChange({
                        ...props.activeRoleSizes.value,
                        depth2: {
                            ...props.activeRoleSizes.value.depth2,
                            values: v
                        }
                    })
                }}
                setDefaultValue={(v: number) => {
                    props.activeRoleSizes.onChange({
                        ...props.activeRoleSizes.value,
                        depth2: {
                            ...props.activeRoleSizes.value.depth2,
                            default: v
                        }
                    })
                }}
            />
            <CornerCabinetSize
                name='Back Width 1' values={props.activeRoleSizes.value.backWidth1.values} defaultValue={props.activeRoleSizes.value.backWidth1.default}
                setValues={(v: number[]) => {
                    props.activeRoleSizes.onChange({
                        ...props.activeRoleSizes.value,
                        backWidth1: {
                            ...props.activeRoleSizes.value.backWidth1,
                            values: v
                        }
                    })
                }}
                setDefaultValue={(v: number) => {
                    props.activeRoleSizes.onChange({
                        ...props.activeRoleSizes.value,
                        backWidth1: {
                            ...props.activeRoleSizes.value.backWidth1,
                            default: v
                        }
                    })
                }}
            />
            <CornerCabinetSize
                name='Back Width 2' values={props.activeRoleSizes.value.backWidth2.values} defaultValue={props.activeRoleSizes.value.backWidth2.default}
                setValues={(v: number[]) => {
                    props.activeRoleSizes.onChange({
                        ...props.activeRoleSizes.value,
                        backWidth2: {
                            ...props.activeRoleSizes.value.backWidth2,
                            values: v
                        }
                    })
                }}
                setDefaultValue={(v: number) => {
                    props.activeRoleSizes.onChange({
                        ...props.activeRoleSizes.value,
                        backWidth2: {
                            ...props.activeRoleSizes.value.backWidth2,
                            default: v
                        }
                    })
                }}
            />

            <Divider style={{ width: '80%', marginTop: 15, marginBottom: 5 }} />

        </Box>
    )
}

export default CornerCategoryUIBox